import {addMonths, format} from 'date-fns';
import {ptBR} from 'date-fns/locale';
import {useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {useSetRecoilState} from 'recoil';

import {logEventBooking} from 'main/analytics/analytics';
import {LogEventBookingEnum} from 'main/analytics/logEventEnum';
import {
	makeGetExamsBooked,
	makePostExamBooking,
	makePostHealthScheduledAppointments,
	makePostOdontoBookingConfirm,
} from 'main/factories/usecases';
import {
	FacialBiometricEnum,
	FeaturesEnum,
	MyAppointmentsEnum,
	OperationStateType,
	OperationStateTypesEnum,
} from 'presentation/constants';
import {OperationStateErrorType} from 'presentation/constants/operationStateErrorEnum';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useGetHomeQuery} from 'presentation/pages/private/home/query/useGetHomeQuery';
import {useAccountDataStore} from 'presentation/pages/private/store/useAccountDataStateStore';
import {refresherState} from 'presentation/pages/states';
import {getHealthOpenScheduling} from 'presentation/services/getHealthOpenScheduling';
import extractTimeFromUTC from 'presentation/utils/extractTimeFromUTC';
import {
	BookingTypeEnum,
	CONFIRMATION_PATH,
	EntryPointCodeEnum,
	FlowEnum,
	TELEMEDICINE_INFO,
} from '../../constants';

import {HandleConfirmationParams, UseBookingProps} from './types';
import {resolveBookingError} from 'presentation/modules/booking/shared/utils/resolveBookingError';
import {useFacialBiometricFeatureFlag} from 'presentation/pages/private/hooks/useFacialBiometricFeatureFlag';
import {useFacialBiometricsStore} from 'presentation/modules/booking/shared/store/useFacialBiometricsStore';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';

export function useBooking({flowType}: UseBookingProps) {
	const bookingData = useBookingStore(state => state.bookingData);
	const setBookingData = useBookingStore(state => state.setBookingData);
	const [bookingState, setBookingState] = useState<OperationStateType | null>(null);
	const [typeError, setTypeError] = useState<OperationStateErrorType | null>(null);
	const navigate = useNavigate();
	const {search} = useLocation();
	const {refetch} = useGetHomeQuery();
	const {
		beneficiary: {idadeC},
		companyPortal,
	} = useAccountDataStore(state => state.accountData);
	const hasFacialBiometrics = useFacialBiometricsStore(state => state.hasFacialBiometrics);
	const registerLater = useFacialBiometricsStore(state => state.registerLater);
	const {isEnabled: isFacialBiometricEnabled} = useFacialBiometricFeatureFlag();
	const {isEnabled: isHideConfirmationBookingModalEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_HIDE_CONFIRMATION_BOOKING_MODAL,
	);

	const setRefresher = useSetRecoilState(refresherState);

	const isTelehealth = flowType === FlowEnum.TELEHEALTH;

	async function handleConfirmOperation({
		id,
		bookingType,
		protocolNumber,
	}: HandleConfirmationParams) {
		setBookingData(prev => ({
			...prev,
			protocolNumber,
			bookingType,
			bookingNumber: id,
		}));

		await refetch();
		setRefresher(value => value + 1);

		if (isHideConfirmationBookingModalEnabled) {
			const path = CONFIRMATION_PATH[bookingType].replace(':id', id);

			navigate(path);
			setBookingState(null);
		} else {
			setBookingState(OperationStateTypesEnum.DONE);
		}
	}

	async function proceedWithHealthBooking() {
		const formattedHour = extractTimeFromUTC(bookingData.hourAt);
		const formattedDate = `${bookingData.dateAt} ${formattedHour}`;

		const {content} = await makePostHealthScheduledAppointments().post({
			specialtyCode: Number(bookingData.specialtyCode),
			subSpecialtyCode: bookingData.subSpecialtyCode
				? Number(bookingData.subSpecialtyCode)
				: undefined,
			providerCode: Number(bookingData.professionalCode),
			clinicCode: isTelehealth
				? Number(TELEMEDICINE_INFO.code)
				: Number(bookingData.unitCode),
			date: formattedDate,
			entryPointCode: Number(EntryPointCodeEnum.V2),
			cellPhoneNumber: bookingData.phone,
			email: bookingData.email,
			operatorName: companyPortal?.operatorName,
			operatorEmail: companyPortal?.operatorEmail,
			operatorDocument: companyPortal?.operatorDocument,
		});

		logEventBooking({
			eventName: isTelehealth
				? LogEventBookingEnum.PORTAL_TELE_BOOKING_COMPLETE
				: LogEventBookingEnum.PORTAL_HEALTH_BOOKING_COMPLETE,
		});

		handleConfirmOperation({
			bookingType: isTelehealth ? BookingTypeEnum.TELEHEALTH : BookingTypeEnum.HEALTH,
			id: content.number.toString(),
			protocolNumber: content.protocol.toString(),
		});
	}

	async function bookHealth(skipOpenSchedulingCheck = false) {
		if (isTelehealth && !hasFacialBiometrics && isFacialBiometricEnabled && !registerLater) {
			navigate({pathname: FacialBiometricEnum.FACIAL_BIOMETRIC, search});
			return;
		}

		try {
			setBookingState(OperationStateTypesEnum.SUBMITTING);

			if (!skipOpenSchedulingCheck) {
				const {beneficiary} = useAccountDataStore.getState().accountData;

				const openSchedulingData = await getHealthOpenScheduling({
					userCode: beneficiary.cdUsuario,
					specialtyCode: Number(bookingData.specialtyCode),
				});

				if (openSchedulingData) {
					setBookingState(OperationStateTypesEnum.OPEN_SCHEDULING);
					return;
				}
			}

			await proceedWithHealthBooking();
		} catch (error) {
			const {state, errorCode} = resolveBookingError(error);
			if (errorCode) setTypeError(errorCode);
			setBookingState(state);
		}
	}

	async function bookOdonto() {
		try {
			setBookingState(OperationStateTypesEnum.SUBMITTING);

			const {consultaMarcada} = await makePostOdontoBookingConfirm().post({
				cdEspecialidade: Number(bookingData.specialtyCode),
				cdSubEspecialidade: bookingData.subSpecialtyCode
					? Number(bookingData.subSpecialtyCode)
					: 0,
				cdPrestadorFisico: bookingData.professionalCode,
				cdPrestadorJuridico: bookingData.unitCode,
				dtConsulta: bookingData.dateAt,
				horarioConsulta: bookingData.hourAt,
				cdPontoEntrada: EntryPointCodeEnum.V1,
			});

			logEventBooking({eventName: LogEventBookingEnum.PORTAL_DENTAL_BOOKING_COMPLETE});

			handleConfirmOperation({
				bookingType: BookingTypeEnum.ODONTO,
				id: consultaMarcada.numero.toString(),
				protocolNumber: consultaMarcada.nuProtocolo.toString(),
			});
		} catch (error) {
			const {state, errorCode} = resolveBookingError(error);
			if (errorCode) setTypeError(errorCode);
			setBookingState(state);
		}
	}

	async function bookExam() {
		try {
			setBookingState(OperationStateTypesEnum.SUBMITTING);

			await makePostExamBooking().post({
				cdTipoExame: Number(bookingData.specialtyCode),
				cdPrestadorFisico: bookingData.professionalCode,
				cdPrestadorJuridico: bookingData.unitCode,
				dtAgendamento: bookingData.dateAt,
				horarioAgendamento: bookingData.hourAt,
				nuCelular: bookingData.phone,
				nuIdade: idadeC,
				cdPontoEntrada: EntryPointCodeEnum.V1,
				email: bookingData.email,
				nmOperador: companyPortal?.operatorName,
				emailOperador: companyPortal?.operatorEmail,
				documentoOperador: companyPortal?.operatorDocument,
			});

			const {exames} = await makeGetExamsBooked().get({
				cdStatus: '0',
				dtFinal: format(addMonths(new Date(), 12), 'P', {locale: ptBR}),
				dtInicial: format(new Date(), 'P', {locale: ptBR}),
			});

			const bookedExam = exames.find(exam => exam.cdTipoExame === bookingData.specialtyCode);

			logEventBooking({eventName: LogEventBookingEnum.PORTAL_EXAM_BOOKING_COMPLETE});

			if (!bookedExam) {
				navigate(MyAppointmentsEnum.MY_APPOINTMENTS);
				return;
			}

			handleConfirmOperation({
				bookingType: BookingTypeEnum.EXAM,
				id: bookedExam.nuExame.toString(),
				protocolNumber: bookedExam.nuProtocolo.toString(),
			});
		} catch (error) {
			const {state, errorCode} = resolveBookingError(error);
			if (errorCode) setTypeError(errorCode);
			setBookingState(state);
		}
	}

	return {
		bookHealth,
		bookExam,
		bookOdonto,
		bookingState,
		setBookingState,
		typeError,
		setTypeError,
	};
}
